<template>
  <div class="py-4 container-fluid realtime">
    <!-- Header + Controls -->
    <div class="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
      <div>
        <div class="d-flex align-items-center gap-2">
          <h3 class="mb-0 fw-bold text-dark">Realtime Dashboard</h3>
          <span class="live-badge" :class="{ paused: !running }">
            <span class="dot"></span>{{ running ? "LIVE" : "PAUSED" }}
          </span>
        </div>
        <p class="text-sm text-muted mb-0 mt-1">
          ผู้เล่น / ยอดแทง {{ gameFilter.length ? `เกม ${gameFilter.join(", ")}` : "รวมทุกเกม" }}
          — อัปเดตทุก {{ intervalLabel }}
          <template v-if="resp">
            · ล่าสุด {{ formatTime(resp.generated_at) }} · query
            {{ resp.query_ms }}ms / {{ resp.summary?.total_games ?? 0 }} เกม
          </template>
        </p>
      </div>

      <div class="d-flex flex-wrap align-items-center gap-2">
        <div class="seg">
          <button
            v-for="r in RANGES"
            :key="r.value"
            type="button"
            :class="{ active: range === r.value }"
            @click="setRange(r.value)"
          >
            {{ r.label }}
          </button>
        </div>

        <GameMultiSelect v-model="gameFilter" :options="gameOptions" @change="onFilterChange" />

        <select v-model.number="intervalMs" class="form-select form-select-sm ctl-select" @change="restartPolling">
          <option v-for="i in INTERVALS" :key="i.value" :value="i.value">
            ทุก {{ i.label }}
          </option>
        </select>

        <button type="button" class="btn btn-sm mb-0" :class="running ? 'btn-danger' : 'btn-success'" @click="toggleRunning">
          {{ running ? "❚❚ หยุด" : "▶ เริ่ม" }}
        </button>
        <button type="button" class="btn btn-sm btn-primary mb-0" :disabled="loading" @click="refresh">
          {{ loading ? "กำลังโหลด..." : "รีเฟรช" }}
        </button>
      </div>
    </div>

    <!-- Summary Cards: ตัวเลขทั้งหมดเป็นของ stats_window (range 5s = 5 วิล่าสุด, range อื่น = ทั้งช่วง) -->
    <p class="text-sm text-muted mb-2">
      สถิติ: <span class="fw-bold">{{ statsLabel }}</span>
    </p>
    <div class="row g-3 mb-2">
      <div v-for="c in summaryCards" :key="c.title" class="col-xl-2 col-md-4 col-6">
        <div class="card h-100">
          <div class="card-body p-3">
            <p class="text-sm text-muted mb-1">{{ c.title }}</p>
            <h4 class="fw-bold mb-0" :class="c.color">{{ c.value }}</h4>
            <p v-if="c.sub" class="text-xs text-muted mb-0 mt-1">{{ c.sub }}</p>
          </div>
        </div>
      </div>
    </div>
    <p class="text-sm text-muted mb-3">
      Txn ล่าสุดทั้งระบบ:
      {{ summary.last_txn_at ? `${formatTime(summary.last_txn_at)} (${timeAgo(summary.last_txn_at)})` : "-" }}
    </p>

    <!-- Charts -->
    <div class="row g-3">
      <div class="col-lg-6">
        <div class="card h-100">
          <div class="card-body p-3">
            <h6 class="fw-bold mb-2">ยอดแทงต่อช่วงเวลา (THB)</h6>
            <div class="chart-box">
              <Bar :data="betChartData" :options="chartOptions" />
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="card h-100">
          <div class="card-body p-3">
            <h6 class="fw-bold mb-2">ผู้เล่นต่อช่วงเวลา (unique)</h6>
            <div class="chart-box">
              <Bar :data="playerChartData" :options="playerChartOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex align-items-center gap-2 mt-3 mb-3">
      <span class="text-sm text-muted">
        คลิกแท่งกราฟเพื่อดูรายชื่อผู้เล่นในช่วงนั้น
        <template v-if="resp?.window">
          · กราฟ {{ formatTime(resp.window.from) }}–{{ formatTime(resp.window.to) }} ({{ series.length }} แท่ง แท่งละ {{ bucketLabel }})
        </template>
      </span>
      <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="openPlayers(null)">
        ดู user ทั้งกราฟ
      </button>
    </div>

    <!-- Players Panel -->
    <div v-if="playerPanel.open" class="card mb-3">
      <div class="card-header pb-2 d-flex justify-content-between align-items-start">
        <div>
          <h6 class="fw-bold mb-0">
            ผู้เล่นช่วง {{ playerPanel.label }}
            <span class="text-muted fw-normal">
              · {{ playerPanel.data?.total ?? 0 }} คน
              <template v-if="(playerPanel.data?.players?.length ?? 0) < (playerPanel.data?.total ?? 0)">
                (แสดง {{ playerPanel.data.players.length }} รายการแรก)
              </template>
            </span>
          </h6>
          <p class="text-xs text-muted mb-0">
            ดึงครั้งเดียวตอนคลิก (ไม่อัปเดตอัตโนมัติ)
            <template v-if="playerPanel.data"> · query {{ playerPanel.data.query_ms }}ms</template>
          </p>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="playerPanel.loading" @click="fetchPlayers">
            รีเฟรช
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="playerPanel.open = false">
            ปิด
          </button>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table align-items-center mb-0 text-dark rt-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Agent</th>
              <th>เกม</th>
              <th class="text-end">Txn</th>
              <th class="text-end">Bet</th>
              <th class="text-end">Min / Max Bet</th>
              <th class="text-end">Win</th>
              <th class="text-end">Win/Loss</th>
              <th class="text-end">แรก–ล่าสุด</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="playerPanel.loading">
              <td colspan="9" class="text-center">🔄 กำลังโหลด...</td>
            </tr>
            <tr v-else-if="!playerPanel.data?.players?.length">
              <td colspan="9" class="text-center">ไม่มีข้อมูล</td>
            </tr>
            <tr v-else v-for="(p, i) in playerPanel.data.players" :key="`${p.username}-${p.game_code}-${i}`">
              <td class="fw-bold">{{ p.username }}</td>
              <td class="text-sm text-muted">{{ p.agent_name }}</td>
              <td>{{ p.game_code }} {{ p.game }}</td>
              <td class="text-end">{{ formatInt(p.txn) }}</td>
              <td class="text-end">{{ formatMoney(p.bet) }} <span class="text-xs text-muted">THB</span></td>
              <td class="text-end">{{ formatMoneyOrDash(p.min_bet) }} <span class="text-muted">/</span> {{ formatMoneyOrDash(p.max_bet) }}</td>
              <td class="text-end">{{ formatMoney(p.win) }}</td>
              <td class="text-end fw-bold" :class="signClass(p.win_loss)">{{ formatSigned(p.win_loss) }}</td>
              <td class="text-end text-sm text-muted">{{ formatTime(p.first_txn_at) }}–{{ formatTime(p.last_txn_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Games Table -->
    <div class="card">
      <div class="card-header pb-2 d-flex justify-content-between align-items-center">
        <h6 class="fw-bold mb-0">
          รายเกม ({{ games.length }} เกมที่มีคนเล่น)
          <span class="text-xs text-muted fw-normal">· {{ statsLabel }}</span>
        </h6>
        <span class="text-xs text-muted">คลิกหัวคอลัมน์เพื่อเรียง</span>
      </div>
      <div class="table-responsive">
        <table class="table align-items-center mb-0 text-dark rt-table">
          <thead>
            <tr>
              <th v-for="col in GAME_COLS" :key="col.key" :class="[col.align, 'sortable', { sorted: sort.key === col.key }]" @click="setSort(col.key)">
                {{ col.label }}
                <span v-if="sort.key === col.key">{{ sort.dir === "desc" ? "↓" : "↑" }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!games.length">
              <td :colspan="GAME_COLS.length" class="text-center">
                {{ loading && !resp ? "🔄 กำลังโหลด..." : "ไม่มีข้อมูล" }}
              </td>
            </tr>
            <tr v-for="g in sortedGames" :key="g.game_code">
              <td>
                <span class="fw-bold">{{ g.game_code }}</span>
                <button type="button" class="filter-link ms-2" @click="filterGame(g.game_code)">
                  {{ gameFilter.includes(g.game_code) ? "ยกเลิกกรอง" : "กรองเกมนี้" }}
                </button>
                <div class="text-sm text-muted">{{ g.game }}</div>
              </td>
              <td class="text-end">{{ formatInt(g.players) }}</td>
              <td class="text-end">{{ formatInt(g.txn) }}</td>
              <td class="text-end bet-cell">
                {{ formatMoney(g.bet) }}
                <div class="bet-bar"><div :style="{ width: betPercent(g.bet) + '%' }"></div></div>
              </td>
              <td class="text-end">{{ formatMoneyOrDash(g.min_bet) }} <span class="text-muted">/</span> {{ formatMoneyOrDash(g.max_bet) }}</td>
              <td class="text-end">{{ formatMoney(g.win) }}</td>
              <td class="text-end fw-bold" :class="g.ggr < 0 ? 'text-danger' : ''">{{ formatMoney(g.ggr) }}</td>
              <td class="text-end">{{ formatPctOrDash(g.rtp) }}</td>
              <td class="text-end text-sm text-muted">{{ timeAgo(g.last_txn_at) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import axios from "axios";
import moment from "moment";
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";
import { HIDDEN_GAME_CODES } from "@/constants/hiddenGames";
import GameMultiSelect from "./components/GameMultiSelect.vue";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const API_BASE = "https://dashboard-api.linkstudio.io/api/v1/game/control";
const GAMECONFIG_URL = `${API_BASE}/gameconfig`;

const RANGES = [
  { value: "5s", label: "5 วิ" },
  { value: "today", label: "วันนี้" },
];
const INTERVALS = [
  { value: 5_000, label: "5 วิ" },
  { value: 10_000, label: "10 วิ" },
  { value: 30_000, label: "30 วิ" },
  { value: 300_000, label: "5 นาที" },
  { value: 600_000, label: "10 นาที" },
  { value: 1_800_000, label: "30 นาที" },
];
const GAME_COLS = [
  { key: "game_code", label: "เกม", align: "" },
  { key: "players", label: "ผู้เล่น", align: "text-end" },
  { key: "txn", label: "Txn", align: "text-end" },
  { key: "bet", label: "Bet (THB)", align: "text-end" },
  { key: "max_bet", label: "Min / Max Bet", align: "text-end" },
  { key: "win", label: "Win (THB)", align: "text-end" },
  { key: "ggr", label: "GGR", align: "text-end" },
  { key: "rtp", label: "RTP", align: "text-end" },
  { key: "last_txn_at", label: "ล่าสุด", align: "text-end" },
];

const range = ref("5s");
const gameFilter = ref([]);
const intervalMs = ref(5_000);
const running = ref(true);
const loading = ref(false);
const resp = ref(null);
const gameList = ref([]);
const now = ref(Date.now());
const sort = reactive({ key: "bet", dir: "desc" });

const playerPanel = reactive({
  open: false,
  loading: false,
  label: "",
  from: null,
  to: null,
  data: null,
});

let pollTimer = null;
let clockTimer = null;
let requestSeq = 0;

// ---- derived ----
const summary = computed(() => resp.value?.summary ?? {});
// backend ส่งกราฟทั้ง window มาครบทุกรอบ (5s = 15 นาที / 180 แท่ง, แท่งว่างเป็น 0) → ใช้ตรงๆ ได้เลย
const series = computed(() => resp.value?.series ?? []);
const bucketLabel = computed(() => {
  const sec = resp.value?.bucket_seconds ?? 0;
  if (sec < 60) return `${sec} วิ`;
  if (sec < 3600) return `${sec / 60} นาที`;
  return `${sec / 3600} ชม.`;
});
// ช่วงเวลาของการ์ดสรุป + ตารางรายเกม
const statsLabel = computed(() => {
  const sw = resp.value?.stats_window;
  const w = resp.value?.window;
  if (!sw) return `ทั้งช่วง ${rangeLabel.value}`;
  const span = `${formatTime(sw.from)}–${formatTime(sw.to)}`;
  if (w && sw.from === w.from && sw.to === w.to) return `ทั้งช่วง ${rangeLabel.value} (${span})`;
  const sec = Math.round((Date.parse(sw.to) - Date.parse(sw.from)) / 1000);
  return `${sec} วิล่าสุด (${span})`;
});
const games = computed(() => resp.value?.games ?? []);
const intervalLabel = computed(
  () => INTERVALS.find((i) => i.value === intervalMs.value)?.label ?? ""
);
const rangeLabel = computed(
  () => RANGES.find((r) => r.value === range.value)?.label ?? range.value
);

const gameOptions = computed(() => {
  if (gameList.value.length) return gameList.value;
  return games.value.map((g) => ({ code: g.game_code, name: g.game }));
});

const summaryCards = computed(() => {
  const s = summary.value;
  return [
    { title: "ผู้เล่น (unique)", value: formatInt(s.players), sub: gameFilter.value.length ? `${gameFilter.value.length} เกมที่เลือก` : "ทุกเกม ทุกสกุลเงิน" },
    { title: "เกมที่มีคนเล่น", value: formatInt(s.active_games), sub: `จาก ${formatInt(s.total_games)} เกม` },
    { title: "จำนวน Txn", value: formatInt(s.txn) },
    { title: "ยอดแทง (THB)", value: formatMoney(s.bet), sub: s.min_bet != null ? `min ${formatMoney(s.min_bet)} / max ${formatMoney(s.max_bet)}` : "" },
    { title: "ยอดจ่าย (THB)", value: formatMoney(s.win) },
    { title: "GGR (THB)", value: formatMoney(s.ggr), sub: `RTP ${formatPctOrDash(s.rtp)}`, color: (s.ggr ?? 0) < 0 ? "text-danger" : "" },
  ];
});

const maxGameBet = computed(() =>
  games.value.reduce((m, g) => Math.max(m, Number(g.bet) || 0), 0)
);

const sortedGames = computed(() => {
  const list = [...games.value];
  const { key, dir } = sort;
  const mul = dir === "desc" ? -1 : 1;
  list.sort((a, b) => {
    let va = a[key];
    let vb = b[key];
    if (key === "last_txn_at") {
      va = va ? Date.parse(va) : 0;
      vb = vb ? Date.parse(vb) : 0;
    }
    if (typeof va === "string" || typeof vb === "string") {
      return String(va ?? "").localeCompare(String(vb ?? ""), undefined, { numeric: true }) * mul;
    }
    return ((Number(va) || 0) - (Number(vb) || 0)) * mul;
  });
  return list;
});

// ---- charts ----
const chartLabels = computed(() => {
  const fmt = range.value === "5s" ? "HH:mm:ss" : "HH:mm";
  return series.value.map((b) => moment(b.time).format(fmt));
});

const betChartData = computed(() => ({
  labels: chartLabels.value,
  datasets: [
    {
      label: "ยอดแทง (THB)",
      data: series.value.map((b) => b.bet),
      backgroundColor: "#3b5bdb",
      borderRadius: 3,
    },
  ],
}));

const playerChartData = computed(() => ({
  labels: chartLabels.value,
  datasets: [
    {
      label: "ผู้เล่น",
      data: series.value.map((b) => b.players),
      backgroundColor: "#2f9e8f",
      borderRadius: 3,
    },
  ],
}));

const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  plugins: {
    legend: { display: false },
    datalabels: { display: false },
    tooltip: {
      callbacks: {
        title: (items) => {
          const b = series.value[items[0]?.dataIndex];
          return b ? `${formatTime(b.from)} – ${formatTime(b.to)}` : "";
        },
        afterBody: (items) => {
          const b = series.value[items[0]?.dataIndex];
          return b ? [`Txn: ${formatInt(b.txn)}`, `Bet: ${formatMoney(b.bet)}`, `Win: ${formatMoney(b.win)}`] : [];
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { maxTicksLimit: 8, maxRotation: 0 } },
    y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.05)" } },
  },
  onClick: (_evt, elements) => {
    if (!elements.length) return;
    openPlayers(series.value[elements[0].index]);
  },
  onHover: (evt, elements) => {
    if (evt.native?.target) evt.native.target.style.cursor = elements.length ? "pointer" : "default";
  },
};
const chartOptions = baseChartOptions;
const playerChartOptions = {
  ...baseChartOptions,
  scales: {
    ...baseChartOptions.scales,
    y: { ...baseChartOptions.scales.y, ticks: { precision: 0 } },
  },
};

// ---- helpers ----
function formatInt(n) {
  return Number(n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}
function formatMoney(n) {
  return Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// min/max bet, rtp เป็น null ได้ (เช่นมีแต่ freespin ไม่มี bet) → แสดง "-"
function formatMoneyOrDash(n) {
  return n == null ? "-" : formatMoney(n);
}
function formatPctOrDash(n) {
  return n == null ? "-" : formatPct(n);
}
function formatSigned(n) {
  const v = Number(n ?? 0);
  return (v > 0 ? "+" : "") + formatMoney(v);
}
function formatPct(n) {
  return `${Number(n ?? 0).toFixed(1)}%`;
}
function formatTime(d) {
  return d ? moment(d).format("HH:mm:ss") : "-";
}
function signClass(n) {
  const v = Number(n ?? 0);
  return v > 0 ? "text-success" : v < 0 ? "text-danger" : "";
}
function betPercent(bet) {
  if (!maxGameBet.value) return 0;
  return Math.max(0, Math.min(100, ((Number(bet) || 0) / maxGameBet.value) * 100));
}
function timeAgo(d) {
  if (!d) return "-";
  const sec = Math.max(0, Math.floor((now.value - Date.parse(d)) / 1000));
  if (sec < 60) return `${sec} วิที่แล้ว`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.floor(min / 60);
  return `${hr} ชม.ที่แล้ว`;
}
function filterParams() {
  const params = {};
  if (gameFilter.value.length) params.game = gameFilter.value.join(",");
  return params;
}

// ---- data ----
async function fetchData() {
  const seq = ++requestSeq;
  loading.value = true;
  try {
    const res = await axios.get(`${API_BASE}/dashboard/realtime`, {
      params: { range: range.value, ...filterParams() },
    });
    // ทิ้งผลของ request เก่าที่ตอบกลับช้ากว่า (เช่นเปลี่ยน range ระหว่างรอ)
    if (seq === requestSeq) resp.value = res.data;
  } catch (err) {
    console.error("โหลด realtime ไม่สำเร็จ", err);
  } finally {
    if (seq === requestSeq) loading.value = false;
  }
}

async function fetchPlayers() {
  playerPanel.loading = true;
  try {
    const params = { ...filterParams() };
    if (playerPanel.from) {
      params.from = playerPanel.from;
      params.to = playerPanel.to;
    } else {
      params.range = range.value;
    }
    const res = await axios.get(`${API_BASE}/dashboard/realtime/players`, { params });
    playerPanel.data = res.data;
  } catch (err) {
    console.error("โหลดรายชื่อผู้เล่นไม่สำเร็จ", err);
    playerPanel.data = null;
  } finally {
    playerPanel.loading = false;
  }
}

function openPlayers(bucket) {
  playerPanel.open = true;
  playerPanel.data = null;
  if (bucket) {
    playerPanel.from = bucket.from;
    playerPanel.to = bucket.to;
    playerPanel.label = `${formatTime(bucket.from)}–${formatTime(bucket.to)}`;
  } else if (resp.value?.window) {
    // ทั้งกราฟ = window ของ series (range 5s คือ 15 นาที ไม่ใช่แค่ 5 วิ)
    const w = resp.value.window;
    playerPanel.from = w.from;
    playerPanel.to = w.to;
    playerPanel.label = `ทั้งกราฟ ${formatTime(w.from)}–${formatTime(w.to)}`;
  } else {
    playerPanel.from = null;
    playerPanel.to = null;
    playerPanel.label = `ทั้งช่วง ${rangeLabel.value}`;
  }
  fetchPlayers();
}

// ---- polling ----
// ใช้ setTimeout ต่อกันหลัง request เสร็จ เพื่อไม่ให้ request ซ้อนกันเวลา API ช้า
function schedule() {
  clearTimeout(pollTimer);
  if (!running.value) return;
  pollTimer = setTimeout(async () => {
    await fetchData();
    schedule();
  }, intervalMs.value);
}
function restartPolling() {
  schedule();
}
function toggleRunning() {
  running.value = !running.value;
  if (running.value) refresh();
  else clearTimeout(pollTimer);
}
async function refresh() {
  clearTimeout(pollTimer);
  await fetchData();
  schedule();
}

// ---- actions ----
function setRange(r) {
  if (range.value === r) return;
  range.value = r;
  playerPanel.open = false;
  refresh();
}
function onFilterChange() {
  playerPanel.open = false;
  refresh();
}
function filterGame(code) {
  // กรองเฉพาะเกมนี้ / กดซ้ำเพื่อเอาเกมนี้ออกจากตัวกรอง
  gameFilter.value = gameFilter.value.includes(code)
    ? gameFilter.value.filter((c) => c !== code)
    : [code];
  onFilterChange();
}
function setSort(key) {
  if (sort.key === key) {
    sort.dir = sort.dir === "desc" ? "asc" : "desc";
  } else {
    sort.key = key;
    sort.dir = key === "game_code" ? "asc" : "desc";
  }
}

async function loadGameList() {
  try {
    const res = await axios.post(GAMECONFIG_URL, {});
    gameList.value = (res.data?.data?.list ?? [])
      .filter((g) => g.code && !HIDDEN_GAME_CODES.includes(g.code))
      .map((g) => ({ code: g.code, name: g.name }))
      .sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }));
  } catch (err) {
    console.error("โหลด gameconfig ไม่สำเร็จ", err);
  }
}

onMounted(() => {
  loadGameList();
  refresh();
  clockTimer = setInterval(() => (now.value = Date.now()), 1000);
});

onBeforeUnmount(() => {
  clearTimeout(pollTimer);
  clearInterval(clockTimer);
  running.value = false;
});
</script>

<style scoped>
.live-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #e6f7ee;
  color: #1f9d55;
  font-size: 12px;
  font-weight: 700;
}
.live-badge .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 1.5s infinite;
}
.live-badge.paused {
  background: #f0f2f5;
  color: #8392ab;
}
.live-badge.paused .dot {
  animation: none;
}
@keyframes pulse {
  50% {
    opacity: 0.3;
  }
}
.seg {
  display: inline-flex;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}
.seg button {
  border: none;
  background: transparent;
  padding: 5px 12px;
  font-size: 13px;
  color: #344767;
  cursor: pointer;
}
.seg button.active {
  background: #1a1f36;
  color: #fff;
  font-weight: 600;
}
.ctl-select {
  width: auto;
  min-width: 120px;
}
.chart-box {
  position: relative;
  height: 260px;
}
.rt-table th {
  font-size: 13px;
  font-weight: 600;
  color: #67748e;
  white-space: nowrap;
}
.rt-table td {
  font-size: 14px;
  white-space: nowrap;
}
.rt-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.rt-table th.sorted {
  color: #3b5bdb;
}
/* ใช้ <button> แทน <a> เพราะ dark mode ของ Argon บังคับ <a> ทุกตัวเป็นสีขาว (!important) */
.filter-link {
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  color: #1a73e8;
  text-decoration: underline;
  cursor: pointer;
}
.filter-link:hover {
  color: #0b57d0;
}
:global(.dark-version .filter-link) {
  color: #74a7ff;
}
:global(.dark-version .filter-link:hover) {
  color: #a8c7ff;
}
.bet-cell {
  min-width: 140px;
}
.bet-bar {
  height: 4px;
  margin-top: 4px;
  background: #eef0f4;
  border-radius: 2px;
  overflow: hidden;
}
.bet-bar > div {
  height: 100%;
  background: #3b5bdb;
}

.text-blue-600 {
    --tw-text-opacity: 1 !important;
    color: rgb(37 99 235 / var(--tw-text-opacity, 1)) !important;
}
</style>
